import { useLocalization } from '@lib/localization';

import {
  Box, Button, Divider, Drawer, IconButton, ListItem, ListItemText, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
} from '@pages/CustomPoiTypes.styles';
import { type PoiType } from '@store/api/poiTypeEndpoints';
import { useState } from 'react';
import { TimesIcon, TrashIcon } from 'react-line-awesome';

interface Props {
  open: boolean;
  poiType: PoiType | null;
  /** Every team in the directory; the owner and already-shared teams are filtered out here. */
  teams: { _id: string; name: string }[];
  saving: boolean;
  onClose: () => void;
  onShare: (teamId: string) => void;
  onRevoke: (teamId: string) => void;
}

/**
 * Read-only shares of one type. There is no access level to pick: a share grants
 * `poi_type:get` and nothing else, so the receiving team can never edit or re-share it.
 */
const ShareDrawer: React.FC<Props> = ({ open, poiType, teams, saving, onClose, onShare, onRevoke }) => {
  const { t } = useLocalization();
  const [selected, setSelected] = useState('');

  const nameOf = (teamId: string) => teams.find((team) => team._id === teamId)?.name ?? teamId;

  const shared = poiType?.sharedWith ?? [];
  const available = teams.filter((team) =>
    team._id !== poiType?.teamId && !shared.some((share) => share.teamId === team._id));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 440 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
              {t('Customization_ShareType')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('Customization_ShareReadOnlyHint')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={drawerCloseStyles}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          {shared.length === 0 && (
            <Typography variant="body2" color="text.secondary">{t('Customization_NotSharedYet')}</Typography>
          )}
          {shared.map((share) => (
            <ListItem
              key={share.teamId}
              disableGutters
              secondaryAction={
                <IconButton edge="end" disabled={saving} onClick={() => onRevoke(share.teamId)}>
                  <TrashIcon />
                </IconButton>
              }
            >
              <ListItemText primary={nameOf(share.teamId)} secondary={t('Customization_ReadOnly')} />
            </ListItem>
          ))}

          <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
            <TextField
              select fullWidth size="small"
              label={t('Customization_AddTeam')}
              value={selected}
              disabled={!available.length}
              onChange={(event) => setSelected(event.target.value)}
            >
              {available.map((team) => (
                <MenuItem key={team._id} value={team._id}>{team.name}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              disabled={!selected || saving}
              onClick={() => { onShare(selected); setSelected(''); }}
            >
              {t('Common_Add')}
            </Button>
          </Stack>
        </Box>

        <Divider />

        <Stack direction="row" justifyContent="flex-end" sx={drawerFooterStyles}>
          <Button variant="contained" size="small" disableElevation onClick={onClose} sx={buttonStyles}>
            {t('Common_Close')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ShareDrawer;
